import { type ApiSchemas, call } from '@rental/contracts/client'
import {
  isTimelineDays,
  type StaffLocale,
  TIMELINE_DAY_PRESETS,
  TIMELINE_DEFAULT_DAYS,
  TIMELINE_MAX_DAYS,
} from '@rental/domain'
import { format, formatDateTime, INTL_LOCALE, localized } from '@rental/i18n'
import { cn, Field } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { CalendarX, KeyRound } from 'lucide-react'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { addDays } from '../board'
import { DateStepper } from '../components/DateStepper'
import { EmptyState } from '../components/EmptyState'
import { LookupProblem } from '../components/LookupProblem'
import { PageHeader } from '../components/PageHeader'
import { Fetching } from '../components/Results'
import { Section } from '../components/Section'
import { Guard, LoadState } from '../components/states'
import { StaffInput, StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { useRomeToday } from '../rome-today'
import { dayBoundaries } from '../time'
import { barLines, lanes, nowLine, SLOTS_PER_DAY } from '../timeline'
import { readTimelineDays, rememberTimelineDays } from '../timeline-days'
import { CREATE_ROUTE, type CreateTarget } from './timeline-create'
import {
  type BarDragHandlers,
  type DragGhost,
  type TimelineDrag,
  useTimelineDrag,
} from './timeline-drag'
import { useReschedule } from './useReschedule'
import {
  BAR_ATTRIBUTE,
  type CreateGhost,
  type LaneCreateHandlers,
  useTimelineCreate,
} from './useTimelineCreate'

const route = getRouteApi('/timeline')

type Item = ApiSchemas['TimelineItem']

/**
 * The days' grid, any number of them (OPS-1, up to TIMELINE_MAX_DAYS): the
 * columns are implicit, created by the in-flow items that span them (the day
 * headers above, a stripe per day in every lane), all the same width
 * (`auto-cols-fr`). The bars stand out of the flow and create no column of
 * their own, so the stripes must stay in the flow: a bar on a line no stripe
 * reaches would have no line to stand on.
 */
export const DAYS_GRID = 'grid auto-cols-fr'
/**
 * A day's least width, on its header: 64 px (96 at three days or fewer), so
 * many days scroll sideways under the lanes' labels rather than squeeze into
 * the screen (UI-2d): at 1080 px each of fourteen days had 33 px, its header
 * cut off and a day's booking a sliver. The lanes take the header's width.
 */
const dayWidth = (days: number) => (days <= 3 ? 'min-w-24' : 'min-w-16')
/** The lanes' labels: a plate and its model, 128 px, which the days scroll under. */
const LABEL = 'w-32 shrink-0'

const dayLabels = new Map<string, Intl.DateTimeFormat>()
function formatter(locale: StaffLocale, month: boolean): Intl.DateTimeFormat {
  const key = `${locale}:${month}`
  let found = dayLabels.get(key)
  if (!found) {
    found = new Intl.DateTimeFormat(INTL_LOCALE[locale], {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      ...(month ? { month: 'short' } : {}),
    })
    dayLabels.set(key, found)
  }
  return found
}

/** A day's header, short ("lun 19"): with its month on the first day and on the first of a month. */
export function dayLabel(locale: StaffLocale, date: string, first: boolean): string {
  const month = first || date.endsWith('-01')
  return formatter(locale, month).format(new Date(`${date}T00:00:00Z`))
}

/**
 * The Planning, for whoever reads the fleet (UI-2g): anyone else who types its address reads
 * the refusal under its title, and nothing is asked of the server for it.
 */
export function TimelineScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'fleet.read')} title={m.timeline.title}>
      <TimelineBoard />
    </Guard>
  )
}

/**
 * The fleet timeline (day 7): one row per vehicle, its bookings, turnarounds
 * and blocks as bars; bookings with no vehicle yet in their category's lanes.
 * Every bar of a booking opens it; a block opens its vehicle. UI-2d: each
 * category on its pane, its days a region of their own that scrolls sideways
 * when they do not fit; bars a finger's height; now navy under a gold mark,
 * as on Today's day line.
 */
function TimelineBoard() {
  const { locale, messages: m } = useLocale()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  // Today follows the clock (UI-2g): a board opened on today moves to the new day at midnight.
  const today = useRomeToday()
  const from = search.from ?? today
  // The address names the span; without one, the span this device last showed (OPS-1).
  const [remembered] = useState(() => readTimelineDays())
  const days = search.days ?? remembered ?? TIMELINE_DEFAULT_DAYS
  const boardKey = ['timeline', from, days, search.category ?? null] as const
  const board = useQuery({
    queryKey: boardKey,
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/timeline', {
            params: {
              query: {
                from,
                days,
                ...(search.category ? { categoryId: search.category } : {}),
              },
            },
          }),
        ),
      ),
    placeholderData: keepPreviousData,
  })
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => load(call(api.GET('/v1/categories'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const data = board.data
  const problem = problemOf(board.error)
  const fetching = board.isPlaceholderData
  const categorySelect = useRef<HTMLSelectElement>(null)
  const known = categories.data?.items.some((category) => category.id === search.category)
  const boundaries = useMemo(() => (data ? dayBoundaries(data.from, data.days) : []), [data])
  const dates = data ? Array.from({ length: data.days }, (_, day) => addDays(data.from, day)) : []
  const nowColumn = data ? nowLine(Date.parse(data.now), boundaries) : null

  const go = (next: { from?: string; days?: number; category?: string }) => {
    if (next.days !== undefined) rememberTimelineDays(next.days)
    void navigate({ to: '/timeline', search: { ...search, ...next } })
  }

  return (
    <>
      <PageHeader title={m.timeline.title}>
        <div className="flex flex-col gap-4 pt-2">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <DateStepper
              label={m.timeline.from}
              value={from}
              today={today}
              step={days}
              onChange={(date) => go({ from: date })}
              words={{
                earlier: m.timeline.earlier,
                later: m.timeline.later,
                today: m.timeline.today,
              }}
            />
            <div className="flex flex-wrap items-end gap-3">
              <DaysControl days={days} onChange={(value) => go({ days: value })} />
              <Field label={m.timeline.category}>
                {(control) => (
                  <StaffSelect
                    {...control}
                    ref={categorySelect}
                    value={search.category ?? ''}
                    onChange={(event) => go({ category: event.target.value || undefined })}
                  >
                    <option value="">{m.timeline.allCategories}</option>
                    {search.category && !known ? (
                      // A category the list does not hold (an old link) is shown as such: a
                      // select with no option for its value shows the first, and "all" was
                      // then no change it could hear (UI-2g).
                      <option value={search.category}>
                        {categories.data ? m.timeline.categoryGone : m.app.loading}
                      </option>
                    ) : null}
                    {categories.data?.items.map((category) => (
                      <option key={category.id} value={category.id}>
                        {localized(category.name, locale)}
                      </option>
                    ))}
                  </StaffSelect>
                )}
              </Field>
            </div>
          </div>
          {/* Without its categories the select offers only "all": say why, and a way to ask again. */}
          <LookupProblem
            problem={problemOf(categories.error)}
            what={m.vehicles.categoriesFailed}
            busy={categories.isFetching}
            retry={categories.refetch}
            target={categorySelect}
          />
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <Legend />
            {fetching ? <Fetching /> : null}
          </div>
        </div>
      </PageHeader>

      <LoadState data={data} problem={problem}>
        {(data) =>
          data.categories.length === 0 ? (
            search.category ? (
              // Asked for one category and given none: that one is not there, which is not
              // a fleet with no categories (UI-2g).
              <EmptyState icon={CalendarX} title={m.timeline.categoryGone}>
                {m.timeline.categoryGoneHint}
              </EmptyState>
            ) : (
              <EmptyState icon={CalendarX} title={m.timeline.noCategories}>
                {m.vehicles.fills}
              </EmptyState>
            )
          ) : (
            <div
              ref={scrollTogether}
              className="flex flex-col gap-6"
              aria-busy={fetching || undefined}
            >
              {data.categories.map((category) => (
                <CategoryBlock
                  // A new span or filter starts each category's days at its first day.
                  key={`${category.id}:${data.from}:${data.days}:${search.category ?? ''}`}
                  category={category}
                  boardKey={boardKey}
                  days={data.days}
                  dates={dates}
                  today={today}
                  boundaries={boundaries}
                  nowColumn={nowColumn}
                />
              ))}
            </div>
          )
        }
      </LoadState>
    </>
  )
}

/**
 * The span: a preset, or "Personalizzato…" with the number typed beside it,
 * 1 to TIMELINE_MAX_DAYS, applied when the field is left or Enter is pressed
 * (OPS-1). A number the Planning cannot show is said so, in the app's words,
 * and the days shown stay as they are: the field never guesses. Prezzi asks
 * its span the same way, to the same bound (W2-S S1).
 */
export function DaysControl({
  days,
  onChange,
}: {
  days: number
  onChange: (days: number) => void
}) {
  const { messages: m } = useLocale()
  const field = useRef<HTMLInputElement>(null)
  const preset = (TIMELINE_DAY_PRESETS as readonly number[]).includes(days)
  const [chosen, setChosen] = useState(false)
  const custom = chosen || !preset
  // "Personalizzato…" just chosen: the number is what the counter wants to type next.
  useEffect(() => {
    if (chosen) field.current?.focus()
  }, [chosen])
  const range = format(m.timeline.customRange, { max: TIMELINE_MAX_DAYS })
  const commit = (input: HTMLInputElement) => {
    const value = Number(input.value)
    if (input.value.trim() !== '' && isTimelineDays(value)) {
      input.setCustomValidity('')
      if (value !== days) onChange(value)
      return
    }
    input.setCustomValidity(range)
    input.reportValidity()
  }
  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit(event.currentTarget)
    }
  }
  return (
    <>
      <Field label={m.timeline.days}>
        {(control) => (
          <StaffSelect
            {...control}
            value={custom ? 'custom' : String(days)}
            onChange={(event) => {
              if (event.target.value === 'custom') {
                setChosen(true)
                return
              }
              setChosen(false)
              onChange(Number(event.target.value))
            }}
          >
            {TIMELINE_DAY_PRESETS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
            <option value="custom">{m.timeline.custom}</option>
          </StaffSelect>
        )}
      </Field>
      {custom ? (
        <Field label={m.timeline.customDays} hint={range}>
          {(control) => (
            <StaffInput
              {...control}
              // Keyed by the days shown: Back or a link changes them under the field.
              key={days}
              ref={field}
              type="number"
              inputMode="numeric"
              min={1}
              max={TIMELINE_MAX_DAYS}
              step={1}
              defaultValue={days}
              className="w-24"
              onInput={(event) => event.currentTarget.setCustomValidity('')}
              onKeyDown={onKeyDown}
              onBlur={(event) => commit(event.currentTarget)}
            />
          )}
        </Field>
      ) : null}
    </>
  )
}

/**
 * The categories' days move as one board: swiping one moves them all, so the
 * same day stands over every category. Each region keeps its own scroll for
 * the keyboard and the finger; this only follows it. A sister moved here
 * answers with a scroll of its own a frame later, by when the region under
 * the finger has moved on: that echo is known by where it was sent and goes
 * no further, where it once pulled the swiped region back mid-swipe (UI-2g).
 * A ref callback, as the board comes and goes with its data; once per board.
 */
export function scrollTogether(root: HTMLDivElement | null) {
  if (!root) return
  const sent = new WeakMap<HTMLElement, number>()
  const follow = (event: Event) => {
    const moved = event.target
    if (!(moved instanceof HTMLElement) || !moved.hasAttribute('data-days')) return
    const echo = sent.get(moved)
    sent.delete(moved)
    if (echo === moved.scrollLeft) return
    for (const days of root.querySelectorAll<HTMLElement>('[data-days]')) {
      if (days === moved || days.scrollLeft === moved.scrollLeft) continue
      const before = days.scrollLeft
      days.scrollLeft = moved.scrollLeft
      // Read back: the browser may round or clamp it. Unmoved, it sends no echo.
      if (days.scrollLeft !== before) sent.set(days, days.scrollLeft)
    }
  }
  // Scrolling does not bubble: the board hears its regions on the way down.
  root.addEventListener('scroll', follow, { capture: true, passive: true })
  return () => root.removeEventListener('scroll', follow, { capture: true })
}

/** Now, as Today's day line draws it: a navy line under a gold mark (DESIGN.md, "Colour"). */
function NowMark() {
  return (
    <span className="flex w-4 flex-col items-center">
      <span className="h-1 w-3 rounded-sm bg-mz-gold-500" />
      <span className="h-3 border-s-2 border-brand-strong" />
    </span>
  )
}

/**
 * What the bars mean, each drawn as its bar is. A bar's state is never its
 * colour alone (UI-2g): a vehicle out carries a key, and every bar's name says
 * its state in words. Out of service, the workshop's or a block, is a warning,
 * as a vehicle's state is everywhere else (DESIGN.md, `StatusChip`): no bar is red.
 */
function Legend() {
  const { messages: m } = useLocale()
  const swatch = 'flex size-4 items-center justify-center rounded-sm'
  const entries: [ReactNode, string][] = [
    [<span key="b" className={cn(swatch, 'bg-mz-blue-600')} />, m.timeline.legendConfirmed],
    [
      <span key="o" className={cn(swatch, 'bg-brand-strong text-action-text')}>
        <KeyRound className="size-3" />
      </span>,
      m.timeline.legendOut,
    ],
    [
      <span key="u" className={cn(swatch, 'border-2 border-brand bg-bg')} />,
      m.timeline.legendUnassigned,
    ],
    [
      <span key="t" className={cn(swatch, 'border border-dashed border-text-muted')} />,
      m.timeline.legendTurnaround,
    ],
    [<span key="m" className={cn(swatch, 'bg-warning')} />, m.timeline.legendOutOfService],
    [<NowMark key="n" />, m.timeline.now],
  ]
  return (
    <ul aria-label={m.timeline.legend} className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
      {entries.map(([mark, label]) => (
        <li key={label} className="flex items-center gap-2">
          <span aria-hidden="true" className="flex">
            {mark}
          </span>
          {label}
        </li>
      ))}
    </ul>
  )
}

function CategoryBlock({
  category,
  boardKey,
  days,
  dates,
  today,
  boundaries,
  nowColumn,
}: {
  category: ApiSchemas['TimelineResponse']['categories'][number]
  boardKey: readonly unknown[]
  days: number
  dates: string[]
  today: string
  boundaries: number[]
  nowColumn: number | null
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const headingId = useId()
  const unassignedLanes = lanes(category.unassigned)
  const canReschedule = can(access, 'bookings.reschedule')
  const laneVehicleIds = useMemo<(string | null)[]>(
    () => [...category.vehicles.map((vehicle) => vehicle.id), ...unassignedLanes.map(() => null)],
    [category.vehicles, unassignedLanes],
  )
  const reschedule = useReschedule(category, boardKey)
  const drag = useTimelineDrag({
    boundaries,
    laneVehicleIds,
    canReschedule,
    onProposal: reschedule.onProposal,
  })
  const goCreate = useNavigate()
  const create = useTimelineCreate({
    boundaries,
    categoryId: category.id,
    canCreate: can(access, 'bookings.create'),
    onCreate: (target: CreateTarget) => void goCreate({ to: CREATE_ROUTE, search: target.search }),
  })
  const vehicleCount = category.vehicles.length
  return (
    <Section title={localized(category.name, locale)} headingId={headingId}>
      {/*
        The category's days, named by its heading: when they do not fit they scroll sideways,
        under the lanes' labels, which stay. It takes the focus, so a keyboard can scroll it;
        a bar the focus reaches is scrolled clear of the labels (scroll-ps-32).
      */}
      <section
        aria-labelledby={headingId}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a region that scrolls takes focus to be scrolled without a mouse
        tabIndex={0}
        data-days=""
        // Positioned, as ScrollRegion is: a bar's hidden name stays inside what scrolls. A
        // stacking context of its own (UI-2g): the plates' column, raised over the bars, stays
        // under the top bar, where the same raise later in the page painted over it, and a
        // finger on Menu opened a vehicle.
        className="relative isolate overflow-x-auto scroll-ps-32"
      >
        <div className="w-max min-w-full">
          <div className="flex border-b border-rule">
            <div
              className={cn(
                LABEL,
                'sticky start-0 z-10 border-e border-rule bg-surface-pane-strong',
              )}
            />
            <div className={cn(DAYS_GRID, 'grow')}>
              {dates.map((date, day) => (
                <span
                  key={date}
                  data-day={date}
                  className={cn(
                    'row-start-1 truncate border-s border-rule px-1 py-1 text-xs',
                    dayWidth(days),
                    `col-start-${day * SLOTS_PER_DAY + 1}`,
                    `col-end-${(day + 1) * SLOTS_PER_DAY + 1}`,
                    date === today ? 'bg-surface-well font-bold text-brand' : 'text-text-muted',
                  )}
                >
                  {dayLabel(locale, date, day === 0)}
                </span>
              ))}
              {nowColumn ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'row-start-1 -ms-2 h-1 w-4 self-end rounded-sm bg-mz-gold-500',
                    `col-start-${nowColumn}`,
                  )}
                />
              ) : null}
            </div>
          </div>
          {category.vehicles.length === 0 ? (
            <p className="py-2 text-text-muted">{m.timeline.noVehicles}</p>
          ) : null}
          <ul className="flex flex-col">
            {category.vehicles.map((vehicle, index) => (
              <Lane
                key={vehicle.id}
                label={
                  <Link
                    to="/vehicles/$vehicleId"
                    params={{ vehicleId: vehicle.id }}
                    className="flex min-h-touch min-w-0 flex-col justify-center"
                  >
                    <span className="figures font-bold">{vehicle.plate}</span>
                    <span className="truncate text-xs text-text-muted">
                      {vehicle.make} {vehicle.model}
                    </span>
                  </Link>
                }
                items={vehicle.items}
                vehicleId={vehicle.id}
                days={days}
                boundaries={boundaries}
                nowColumn={nowColumn}
                drag={drag}
                laneIndex={index}
                gridRef={index === 0 ? drag.gridRef : undefined}
                ghost={drag.ghost?.laneIndex === index ? drag.ghost : null}
                createHandlers={create.laneHandlers(vehicle.id, index)}
                createGhost={create.ghost?.laneIndex === index ? create.ghost : null}
              />
            ))}
            {unassignedLanes.map((items, index) => {
              const laneIndex = vehicleCount + index
              return (
                <Lane
                  // Lanes are rebuilt from the data each time: their order is their identity.
                  // biome-ignore lint/suspicious/noArrayIndexKey: derived lanes
                  key={index}
                  // Every lane says what it holds; the eye needs it only on the first.
                  label={
                    <span className={index === 0 ? 'font-medium text-brand' : 'sr-only'}>
                      {m.timeline.unassigned}
                    </span>
                  }
                  items={items}
                  vehicleId={null}
                  days={days}
                  boundaries={boundaries}
                  nowColumn={nowColumn}
                  drag={drag}
                  laneIndex={laneIndex}
                  gridRef={laneIndex === 0 ? drag.gridRef : undefined}
                  ghost={drag.ghost?.laneIndex === laneIndex ? drag.ghost : null}
                  createHandlers={null}
                  createGhost={null}
                />
              )
            })}
          </ul>
        </div>
      </section>
      {/* The reschedule confirm and the drag's spoken status sit at the pane's
          level, outside the days that scroll sideways (UI-2 + timeline drag). */}
      {reschedule.ui}
      {drag.ghost ? (
        <span role="status" className="sr-only">
          {drag.ghost.axis === 'time' || drag.ghost.axis === 'vehicle'
            ? m.timeline.dragMoveHint
            : m.timeline.resizeHint}
        </span>
      ) : null}
      {create.ghost ? (
        <span role="status" className="sr-only">
          {m.timeline.createHint}
        </span>
      ) : null}
    </Section>
  )
}

function Lane({
  label,
  items,
  vehicleId,
  days,
  boundaries,
  nowColumn,
  drag,
  laneIndex,
  gridRef,
  ghost,
  createHandlers,
  createGhost,
}: {
  label: ReactNode
  items: readonly Item[]
  vehicleId: string | null
  days: number
  boundaries: number[]
  nowColumn: number | null
  drag: TimelineDrag
  laneIndex: number
  gridRef?: (node: HTMLElement | null) => void
  ghost: DragGhost | null
  createHandlers: LaneCreateHandlers | null
  createGhost: CreateGhost | null
}) {
  const { messages: m } = useLocale()
  const ghostLines = ghost
    ? barLines(ghost.startsAt.toISOString(), ghost.endsAt.toISOString(), boundaries)
    : null
  const createLines = createGhost
    ? barLines(createGhost.startsAt.toISOString(), createGhost.endsAt.toISOString(), boundaries)
    : null
  return (
    <li className="flex border-b border-rule last:border-b-0">
      <div
        className={cn(
          LABEL,
          'sticky start-0 z-10 flex items-center border-e border-rule bg-surface-pane-strong pe-2',
        )}
      >
        {label}
      </div>
      <div
        ref={gridRef}
        className={cn(
          DAYS_GRID,
          'relative grow items-center',
          // A mouse drag across the lane is the selection; a finger is never one (OPS-1):
          // it pans the days, so the lane leaves every touch to the browser.
          createHandlers && 'select-none cursor-crosshair',
        )}
        {...(createHandlers ?? {})}
      >
        {/* A bar's height whatever the lane holds: the bars stand out of its flow (`BAR`). */}
        <span aria-hidden="true" className="col-start-1 row-start-1 my-1 h-touch" />
        {/* In the flow on purpose: these stripes create the columns (`DAYS_GRID`). */}
        {Array.from({ length: days }, (_, day) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: one stripe per day
            key={day}
            aria-hidden="true"
            className={cn(
              'row-start-1 h-full border-s border-rule',
              `col-start-${day * SLOTS_PER_DAY + 1}`,
              `col-end-${(day + 1) * SLOTS_PER_DAY + 1}`,
            )}
          />
        ))}
        {nowColumn ? (
          <span
            aria-hidden="true"
            data-now=""
            className={cn(
              'row-start-1 h-full border-s-2 border-brand-strong',
              `col-start-${nowColumn}`,
            )}
          />
        ) : null}
        {/* Latest first in the DOM so earlier bars paint over later ones: a short
            booking floored to its least width (min-w-6) reaches into the next
            column, and staying on top keeps it unobscured — WCAG 2.5.8's 24 px
            (UI-2d). Contained to the lane's grid stacking context, under the
            sticky plates column. */}
        {[...items].reverse().map((item) => (
          <Bar
            key={`${item.kind}-${item.startsAt}-${item.booking?.code ?? item.block?.id ?? ''}`}
            item={item}
            vehicleId={vehicleId}
            boundaries={boundaries}
            handlers={drag.barHandlers(item, vehicleId, laneIndex)}
            held={drag.holding !== null && drag.holding === item.booking?.code}
          />
        ))}
        {createLines ? (
          <span
            aria-hidden="true"
            className={cn(
              // min-w-0: an in-flow grid item's content (the hint) would otherwise
              // set its column's min-content and, through the w-max track, widen the
              // whole grid mid-drag — the pointer→day geometry reads that width.
              // Absolute, like the bars: in flow its hint would set the column's
              // max-content and, through the w-max track, widen the grid mid-drag,
              // and the pointer→day geometry reads that width (lands a day early).
              'absolute inset-x-0 inset-y-1 row-start-1 truncate rounded-sm border-2 border-dashed border-brand bg-surface px-1 text-xs leading-body opacity-80 text-brand',
              `col-start-${createLines.start}`,
              `col-end-${createLines.end}`,
            )}
          >
            {m.timeline.createHint}
          </span>
        ) : null}
        {ghostLines ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-x-0 inset-y-1 row-start-1 truncate rounded-sm border-2 border-dashed border-brand bg-bg px-1 text-xs leading-body opacity-80 figures font-bold text-brand',
              `col-start-${ghostLines.start}`,
              `col-end-${ghostLines.end}`,
            )}
          >
            {ghost?.code}
          </span>
        ) : null}
      </div>
    </li>
  )
}

/**
 * A bar is a finger's height, 44 px. Its width is its time, so it is never
 * under 24 px, WCAG 2.5.8's least (DESIGN.md: the one exemption from 44 px,
 * `data-bar`): a turnaround of a few hours would otherwise be a sliver.
 * It stands out of the lane's flow, over the columns of its time (UI-2g): in
 * the flow, its code and its least width set the width of the days, so one
 * short booking widened its category's days and no day stood over the same
 * day in the next category. A bar shorter than 24 px reaches into the next
 * column, under the next bar if one starts there.
 */
const BAR =
  'absolute inset-x-0 top-1 row-start-1 flex h-touch min-w-6 items-center gap-1 rounded-sm px-1 text-xs no-underline'

function Bar({
  item,
  vehicleId,
  boundaries,
  handlers,
  held,
}: {
  item: Item
  vehicleId: string | null
  boundaries: number[]
  handlers: BarDragHandlers | null
  /** A finger has rested on it long enough to lift it (OPS-1): it looks lifted. */
  held: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const openBookings = can(access, 'bookings.read')
  const lines = barLines(item.startsAt, item.endsAt, boundaries)
  if (!lines) return null
  const columns = cn(`col-start-${lines.start}`, `col-end-${lines.end}`)
  // A draggable bar never selects text. A finger that moves at once pans the days
  // (`touch-manipulation`); one that rests on the bar lifts it (timeline-drag.ts, OPS-1).
  const grab = handlers
    ? cn(
        'touch-manipulation select-none cursor-grab timeline-bar-resizable',
        held && 'shadow-chosen ring-1 ring-action-bg',
      )
    : ''
  // The create drag ignores a press that lands on a bar (useTimelineCreate).
  const barMark = { [BAR_ATTRIBUTE]: '' }
  const from = formatDateTime(locale, item.startsAt)
  const to = item.endsAt ? formatDateTime(locale, item.endsAt) : m.timeline.untilFurtherNotice

  if (item.kind === 'buffer') {
    // A turnaround thinner than a column would only cover its neighbours. Drawn with an
    // edge at 3:1, and said (UI-2g): before, a fill of 1.3:1 and nothing for a screen reader.
    return lines.short ? null : (
      <span
        {...barMark}
        className={cn('row-start-1 h-4 rounded-sm border border-dashed border-text-muted', columns)}
      >
        <span className="sr-only">{format(m.timeline.turnaroundBar, { from, to })}</span>
      </span>
    )
  }
  if (item.block) {
    const label = format(m.timeline.blockBar, {
      kind: m.maintenanceKinds[item.block.kind],
      reason: item.block.reason,
      from,
      to,
    })
    // Out of service is a warning, the workshop's or a block alike: no vehicle state is
    // red (DESIGN.md, `StatusChip`). The bar's words say which (UI-2g).
    return vehicleId ? (
      <Link
        to="/vehicles/$vehicleId"
        params={{ vehicleId }}
        data-bar=""
        {...barMark}
        className={cn(BAR, columns, 'bg-warning text-action-text')}
        aria-label={label}
      >
        <span className="min-w-0 truncate">{m.maintenanceKinds[item.block.kind]}</span>
      </Link>
    ) : null
  }
  if (!item.booking) return null
  // The state in words, never in colour alone (UI-2g): booked and out were two blues 2:1 apart.
  const label = format(m.timeline.bar, {
    code: item.booking.code,
    name: item.booking.customerName,
    status: m.status[item.booking.status],
    from,
    to,
  })
  const out = item.booking.status === 'checked_out'
  const tone = !vehicleId
    ? 'border-2 border-brand bg-bg text-brand'
    : out
      ? 'bg-brand-strong text-action-text'
      : 'bg-mz-blue-600 text-action-text'
  // A vehicle out carries a key, as in the legend: the eye needs no colour to tell it.
  const key = out ? <KeyRound aria-hidden="true" className="size-3 shrink-0" /> : null
  // A role that cannot open bookings (the Runner) sees the bar, not a dead link.
  if (!openBookings) {
    return (
      <span
        {...barMark}
        className={cn(BAR, columns, tone, grab, 'figures font-bold')}
        {...(handlers ?? {})}
      >
        {key}
        <span aria-hidden="true" className="min-w-0 truncate">
          {item.booking.code}
        </span>
        <span className="sr-only">{label}</span>
      </span>
    )
  }
  return (
    <Link
      to="/bookings/$code"
      params={{ code: item.booking.code }}
      data-bar=""
      {...barMark}
      className={cn(BAR, columns, tone, grab, 'figures font-bold')}
      aria-label={label}
      // A link is natively draggable; that would hijack the pointer drag on desktop.
      draggable={false}
      {...(handlers ?? {})}
    >
      {key}
      <span className="min-w-0 truncate">{item.booking.code}</span>
    </Link>
  )
}
